import { ICrmOwnerRef } from './crm'

export enum CampaignType {
  HostedEvent = 'Hosted Event',
  ConferenceTradeShow = 'Conference / Trade Show',
  BdTrip = 'BD Trip',
  OutreachList = 'Outreach List',
  MarketingSend = 'Marketing Send',
  Partnership = 'Partnership',
  System = 'System',
  Unclassified = 'Unclassified',
}

export const CAMPAIGN_TYPES = Object.values(CampaignType)

/** Machine-created lists (Pardot mirrors, scraping queues) — kept for history, hidden from people-facing views. */
export const HUMAN_CAMPAIGN_TYPES = CAMPAIGN_TYPES.filter((type) => type !== CampaignType.System)

// A bulk add or rule past this is almost certainly a mis-set filter, not a real audience.
export const MAX_BULK_ADD = 5000

/**
 * Salesforce `Campaign.Type` picklist labels folded into Atlas types. The Salesforce
 * RecordTypeId carries no meaning of its own, so the label is the only signal; a
 * campaign without a known label is Unclassified rather than guessed at.
 */
export const SALESFORCE_CAMPAIGN_TYPE_MAP: Record<string, CampaignType> = {
  'Hosted / Sponsored Event': CampaignType.HostedEvent,
  'Networking Event': CampaignType.HostedEvent,
  Conference: CampaignType.ConferenceTradeShow,
  'Trade Show / Conference': CampaignType.ConferenceTradeShow,
  'BD Trip': CampaignType.BdTrip,
  Outreach: CampaignType.OutreachList,
  'Call - List Purchased': CampaignType.OutreachList,
  'Marketing Campaign': CampaignType.MarketingSend,
  Email: CampaignType.MarketingSend,
  'Marketing Engagement': CampaignType.MarketingSend,
  'Referral Partnership': CampaignType.Partnership,
  Partners: CampaignType.Partnership,
}

export const campaignTypeFromSalesforce = (label: string | null | undefined): CampaignType =>
  (label && SALESFORCE_CAMPAIGN_TYPE_MAP[label.trim()]) || CampaignType.Unclassified

/** A person's, company's or organization's membership in a campaign (CRM network rail row). */
export interface ICampaignMembershipRow {
  id: string // membership row id (stable key)
  campaignId: string | null
  name: string
  campaignType: CampaignType | null
  memberStatus: string | null
  // Salesforce's wording, when it differs from memberStatus (e.g. "Clicked Warby Parker").
  originalStatus: string | null
  // Who a person was when they joined; null for company/organization members.
  joinedTitle: string | null
  joinedAffiliation: string | null
  startDate: string | null
  endDate: string | null
  notes: string | null
  // On a company or organization page: its people who were on the campaign while there.
  via: ICampaignMembershipPerson[]
}

export interface ICampaignMembershipPerson {
  personId: string
  name: string
  status: string | null
}

export enum CampaignMemberStatus {
  // Hosted Event
  Invited = 'Invited',
  Accepted = 'Accepted',
  Attended = 'Attended',
  Declined = 'Declined',
  NoShow = 'No Show',
  Cancelled = 'Cancelled',
  NoResponse = 'No Response',
  // Conference / Trade Show, BD Trip, Outreach List, Partnership
  Suggested = 'Suggested',
  Selected = 'Selected',
  ReachedOut = 'Reached Out',
  Responded = 'Responded',
  MeetingSet = 'Meeting Set',
  Met = 'Met',
  NotNow = 'Not Now',
  // Marketing Send — recorded automatically
  Sent = 'Sent',
  Opened = 'Opened',
  Clicked = 'Clicked',
  Unsubscribed = 'Unsubscribed',
  // System
  Queued = 'Queued',
  InProgress = 'In Progress',
  Done = 'Done',
}

export enum MemberStatusLadder {
  Event = 'event',
  Outreach = 'outreach',
  Send = 'send',
  System = 'system',
}

export const MEMBER_STATUS_LADDERS: Record<MemberStatusLadder, CampaignMemberStatus[]> = {
  [MemberStatusLadder.Event]: [
    CampaignMemberStatus.Invited,
    CampaignMemberStatus.Accepted,
    CampaignMemberStatus.Attended,
    CampaignMemberStatus.Declined,
    CampaignMemberStatus.NoShow,
    CampaignMemberStatus.Cancelled,
    CampaignMemberStatus.NoResponse,
  ],
  [MemberStatusLadder.Outreach]: [
    CampaignMemberStatus.Suggested,
    CampaignMemberStatus.Selected,
    CampaignMemberStatus.ReachedOut,
    CampaignMemberStatus.Responded,
    CampaignMemberStatus.MeetingSet,
    CampaignMemberStatus.Met,
    CampaignMemberStatus.NotNow,
  ],
  [MemberStatusLadder.Send]: [
    CampaignMemberStatus.Sent,
    CampaignMemberStatus.Opened,
    CampaignMemberStatus.Clicked,
    CampaignMemberStatus.Unsubscribed,
  ],
  [MemberStatusLadder.System]: [
    CampaignMemberStatus.Queued,
    CampaignMemberStatus.InProgress,
    CampaignMemberStatus.Done,
  ],
}

/** Unclassified campaigns have no ladder: each legacy status keeps its own nearest meaning. */
export const CAMPAIGN_TYPE_LADDER: Record<CampaignType, MemberStatusLadder | null> = {
  [CampaignType.HostedEvent]: MemberStatusLadder.Event,
  [CampaignType.ConferenceTradeShow]: MemberStatusLadder.Outreach,
  [CampaignType.BdTrip]: MemberStatusLadder.Outreach,
  [CampaignType.OutreachList]: MemberStatusLadder.Outreach,
  [CampaignType.Partnership]: MemberStatusLadder.Outreach,
  [CampaignType.MarketingSend]: MemberStatusLadder.Send,
  [CampaignType.System]: MemberStatusLadder.System,
  [CampaignType.Unclassified]: null,
}

/** Sends record delivery/opens/clicks automatically; System lists are machine-owned. */
export const isMemberStatusEditable = (type: CampaignType): boolean =>
  type !== CampaignType.MarketingSend && type !== CampaignType.System

export const memberStatusOptions = (type: CampaignType): CampaignMemberStatus[] => {
  const ladder = CAMPAIGN_TYPE_LADDER[type]
  return ladder ? MEMBER_STATUS_LADDERS[ladder] : Object.values(CampaignMemberStatus)
}

/** Salesforce's campaign lifecycle values, reused for campaigns created in Atlas. */
export const CAMPAIGN_STATUSES = [
  'Pre Planning',
  'Planned',
  'In Progress',
  'Follow Up',
  'Completed',
  'Closed',
] as const

export enum CampaignMemberKind {
  Person = 'person',
  Company = 'company',
  Organization = 'organization',
}

export enum CampaignMemberSource {
  Salesforce = 'salesforce',
  Manual = 'manual',
  Rule = 'rule',
  Form = 'form',
}

/** What a bulk add or membership rule selects: People-tab filters, or companies-list filters (Radar). */
export enum CampaignRuleAudience {
  People = 'people',
  Companies = 'companies',
}

export interface ICampaignListRow {
  id: string
  name: string | null
  type: CampaignType
  status: string | null
  isActive: boolean | null
  startDate: string | null
  endDate: string | null
  owner: ICrmOwnerRef | null
  memberCount: number
  spend: number
}

export interface ICampaignSeriesLink {
  id: string
  name: string | null
  startDate: string | null
}

export interface ICampaignDetail extends ICampaignListRow {
  description: string | null
  eventPlan: string | null
  reviewNotes: string | null
  highlights: string | null
  lowlights: string | null
  recommendations: string | null
  venue: string | null
  eventQuality: string | null
  attendeeQuality: string | null
  parentId: string | null
  /** The parent campaign plus its other children — the other years of a recurring campaign. */
  series: ICampaignSeriesLink[]
  isFromSalesforce: boolean
  statusCounts: Partial<Record<CampaignMemberStatus, number>>
  referralCount: number
  dealCount: number
  wonDealAmount: number
}

export interface ICampaignMemberCompany {
  id: string
  name: string
}

export interface ICampaignMemberRow {
  id: string
  kind: CampaignMemberKind
  memberId: string | null
  name: string
  status: CampaignMemberStatus | null
  notes: string | null
  // A person's primary company or organization; whichever is set is the one `affiliation` names.
  affiliation: string | null
  affiliationCompanyId: string | null
  affiliationOrganizationId: string | null
  title: string | null
  // Who the person was when they joined, set only where it differs from who they are now.
  previousTitle: string | null
  previousAffiliation: string | null
  // The companies behind the member's deals.
  connectedCompanies: ICampaignMemberCompany[]
  owner: ICrmOwnerRef | null
  ownerName: string | null
  lastActivityDate: string | null
  joinedAt: string | null
  source: CampaignMemberSource
}

export interface ICampaignReferralRow {
  id: string
  name: string | null
  date: string | null
  sourceName: string | null
  targetName: string | null
  targetCompanyId: string | null
  opportunityName: string | null
  opportunityStage: string | null
  opportunityAmount: number | null
}

export interface ICampaignExpenseRow {
  id: string
  name: string | null
  date: string | null
  amount: number | null
  category: string | null
  personName: string | null
  companyName: string | null
}

export interface ICampaignMembershipRule {
  id: string
  campaignId: string
  name: string
  audience: CampaignRuleAudience
  filters: Record<string, unknown>
  lastRunAt: string | null
  lastAddedCount: number | null
}
